import React, { Fragment, useEffect, useState } from "react";
import { string as yupString, object as yupObject } from "yup";
import { withTheme } from "styled-components/native";
import { defineMessages } from "react-intl";
import { withApollo } from "react-apollo";
import { withRouter } from "cci-utility";
import {
  BaseCenteredLogoPage,
  Button,
  Container,
  Icon,
  FormCheckbox,
  FormEmailField,
  FormNumberField,
  FormPasswordField,
  Label,
} from "cci-component";
import {
  forgotPasswordMutation,
  loginMutation,
  isMfaEnabledMutation
} from "cci-logic";
import { Form, ToastContext } from "cci-component";

const messages = defineMessages({
  emailAddress: {
    id: "emailAddress",
    defaultMessage: "E-mail Address"
  },
  password: {
    id: "password",
    defaultMessage: "Password"
  },
  rememberMe: {
    id: "rememberMe",
    defaultMessage: "Remember me"
  },
  forgotPassword: {
    id: "forgotPassword",
    defaultMessage: "Forgot Password?"
  },
  invalidEmail: {
    id: "invalidEmail",
    defaultMessage: "Please enter a valid e-mail address."
  },
  invalidAccount: {
    id: "invalidAccount",
    defaultMessage:
      "We could not locate the account with the e-mail address you entered."
  },
  errorSendingEmail: {
    id: "errorSendingEmail",
    defaultMessage: `There was a problem sending the forgot password e-mail, please contact support. Error: {message}`
  },
  forgotPasswordEmailSent: {
    id: "forgotPasswordEmailSent",
    defaultMessage:
      "A link with instructions to reset the password has been sent to your e-mail."
  },
  login: {
    id: "login",
    defaultMessage: "LOGIN"
  },
  code: {
    id: "code",
    defaultMessage: "Code"
  },
  resendMFACode: {
    id: "resendMFACode",
    defaultMessage: "Resend Code"
  }
});
/**
 * Timer Component. Maybe a TODO is to set a PROP with 60 as the default seconds to count.
 */
const Timer = () => {
  const [counter, setCounter] = useState(60);
  useEffect(() => {
    counter > 0 && setTimeout(() => setCounter(counter - 1), 1000);
  }, [counter]);

  return (
    <Label fontSize={12}>{" " + counter}</Label>
  )
}

/**
 * Login page that prompts for the user's e-mail address and password.
 *
 * Props:
 * loginButtonTitle: if you want a title or icon on login button
 */
const LoginPage = ({ client, theme, loginButtonTitle, hideRememberme }) => {
  const [showCode, setShowCode] = useState(false),
    [resendCode, setResendCode] = useState(false),
    [resendAvailable, setResendAvailable] = useState(true),
    mutate = (email, toast) => {
      client.mutate({
        mutation: isMfaEnabledMutation,
        variables: { email },
        fetchPolicy: "no-cache",
        context: {
          showMessage: toast.show,
          setCode: setShowCode,
          setResendAvailable
        },
        onError: (e) => console.log('e', e),
        onCompleted: (e) => console.log('compl', e)
      })
      setTimeout(() => {
        setResendCode(true)
      }, 200);
    }

  useEffect(() => {
    if (!resendAvailable) {
      setTimeout(() => {
        setResendAvailable(true)
      }, 60000);
    }
  }, [resendAvailable]);

  return (
    <BaseCenteredLogoPage>
      <ToastContext.Consumer>
        {toast => (
          <Form
            delta={false}
            showMessage={toast.show}
            mutation={loginMutation}
            initialValues={{ rememberMe: true }}
            validationSchema={yupObject().shape({
              userId: yupString()
                .required()
                .email("invalidEmailFieldError"),
              // Don't require the password when forgotPassword mutation is called
              password: yupString().when("$forgotPassword", (value, schema) =>
                value === true ? schema : schema.required()
              ),
              code: yupString().test(
                'codeRequired',
                'requiredFieldError',
                (value) => {
                  if (showCode && !value) {
                    return false
                  } else {
                    return true
                  }
                }
              ).matches(/^[0-9]{6}$/, "invalidCodeFormat")
            })}
          >
            {({ handleSubmit, pristine, submitting, form }) => (
              <Fragment>
                {/* E-mail field */}
                <FormEmailField
                  name="userId"
                  leftComponent={
                    <Icon
                      name="envelope"
                      color={theme.fieldIconColor}
                      iconSize={20}
                      style={{ marginLeft: 10 }}
                    />
                  }
                  placeholder={messages.emailAddress}
                  returnKeyType="next"
                  onSubmitEditing={handleSubmit}
                  onBlur={({ nativeEvent: { text: email } }) => {
                    const valid = /[a-z0-9]+@[a-z]+\.[a-z]{2,3}/

                    if (valid.test(email)) {
                      if (resendAvailable) {
                        mutate(email, toast)
                      }
                    } else {
                      setShowCode(false)
                    }
                  }}
                />

                {/* Password field */}
                <FormPasswordField
                  name="password"
                  leftComponent={
                    <Icon
                      name="lock"
                      color={theme.fieldIconColor}
                      iconSize={20}
                      style={{ marginLeft: 10 }}
                    />
                  }
                  margins="30px 0 0 0"
                  returnKeyType="next"
                  placeholder={messages.password}
                  onSubmitEditing={handleSubmit}
                />

                {/* Code field */}
                {showCode && (
                  <Container>
                    <FormNumberField
                      name="code"
                      leftComponent={
                        <Icon
                          name="shield-check"
                          color={theme.fieldIconColor}
                          iconSize={20}
                          style={{ marginLeft: 10 }}
                        />
                      }
                      margins="30px 0 0 0"
                      maxLength={6}
                      autoComplete="off"
                      returnKeyType="go"
                      placeholder={messages.code}
                      onSubmitEditing={handleSubmit}
                    />

                    {/* RESEND 2FA CODE */}
                    {resendCode && (
                      <Container
                        flexDirection="row"
                        style={{ position: "absolute", right: -100, bottom: 10 }}
                      >
                        <Button
                          ui="link"
                          title={messages.resendMFACode}
                          // alignSelf="center"
                          disabled={pristine || submitting || !resendAvailable}
                          // Here we override the mutation to call forgot password instead of the login mutation
                          onPress={() => {
                            const email = form.getFieldState("userId").value,
                              valid = /[a-z0-9]+@[a-z]+\.[a-z]{2,3}/

                            if (valid.test(email)) {
                              if (resendAvailable) {
                                mutate(email, toast)
                              }
                            } else {
                              setShowCode(false)
                              setResendCode(false)
                            }
                          }}
                        />
                        {!resendAvailable && <Timer />}
                      </Container>
                    )}

                  </Container>)}

                {/* Remember me checkbox */}
                {!hideRememberme && (
                  <FormCheckbox
                    name="rememberMe"
                    rightLabel={messages.rememberMe}
                    margins="5px 0 0 10px"
                    labelMargins="0 0 0 15px"
                    backgroundColor="transparent"
                  />
                )}

                {/* Login button */}
                <Button
                  leftIcon={loginButtonTitle ? null : "sign-in"}
                  title={loginButtonTitle ? loginButtonTitle : null}
                  margins="30px 0 0 0"
                  alignSelf="center"
                  disabled={pristine || submitting}
                  onPress={handleSubmit}
                />

                {/* Forgot password link */}
                <Button
                  ui="link"
                  title={messages.forgotPassword}
                  margins="30px 0 0 0"
                  alignSelf="center"
                  disabled={pristine || submitting}
                  // Here we override the mutation to call forgot password instead of the login mutation
                  onPress={() =>
                    handleSubmit({
                      submit: { mutation: forgotPasswordMutation },
                      validationContext: { forgotPassword: true }
                    })
                  }
                />
              </Fragment>
            )}
          </Form>
        )}
      </ToastContext.Consumer>
    </BaseCenteredLogoPage>
  )
};

export default withApollo(withRouter(withTheme(LoginPage)));
